import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Syringe } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { timeOnly } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, DateTimePicker, Field, Input, Select, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { doseIsEarly, doseNeedsReason, givenAtFrom, localDateTimeValue } from '../../dose-timing';
import type { Dose } from '../../admission/types';

const OUTCOMES = ['given', 'held', 'refused', 'missed'] as const;
type Outcome = (typeof OUTCOMES)[number];

export function AdministerDialog({ dose, onClose, onSaved }: { dose: Dose; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [status, setStatus] = useState<Outcome>('given');
  const [witnessId, setWitnessId] = useState('');
  const [reason, setReason] = useState('');
  const [site, setSite] = useState('');
  const [openedAt] = useState(() => new Date());
  const [givenAtValue, setGivenAtValue] = useState(() => localDateTimeValue(openedAt));
  const { user } = useAuth();

  const nurses = useQuery({
    queryKey: ['emar-witnesses'],
    queryFn: () => get<{ id: string; name: string; designation: string | null }[]>('/ipd/emar/witnesses'),
    enabled: dose.order.isHighAlert,
    staleTime: 300_000,
  });
  const witnesses = (nurses.data ?? []).filter((nurse) => nurse.id !== user?.id);

  const givenAt = givenAtFrom(givenAtValue, openedAt);
  const early = status === 'given' && doseIsEarly(dose, givenAt);
  const reasonRequired = doseNeedsReason(dose, status, givenAt);

  const save = useMutation({
    mutationFn: () =>
      post(`/ipd/emar/${dose.id}/administer`, {
        status,
        witnessUserId: witnessId || undefined,
        reason: reason || undefined,
        site: site || undefined,
        administeredAt: status === 'given' ? givenAt.toISOString() : undefined,
      }),
    onSuccess: () => {
      toast.success(tr('ipd', 'dose_recorded'));
      onSaved();
    },
    onError: (error) => toast.error(tr('ipd', 'could_not_record_the_dose'), describeError(error)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      icon={Syringe}
      title={dose.order.label}
      description={tr('ipd', 'x_x_due_x', { dose: dose.order.dose, route: dose.order.route, scheduledAt: timeOnly(dose.scheduledAt) })}
      dirty={reason !== '' || site !== '' || witnessId !== ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {tr('ipd', 'record')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {dose.order.isHighAlert && <Alert variant="warning">{tr('ipd', 'high_alert_medicine_a_second_nurse_must_witness')}</Alert>}

        <Field label={tr('ipd', 'outcome')} required>
          <ChoiceButtons value={status} onChange={setStatus} label={tr('ipd', 'outcome')} options={OUTCOMES.map((value) => ({ value, label: statusLabel(value) }))} />
        </Field>

        {dose.order.isHighAlert && status === 'given' && (
          <Field label={tr('ipd', 'witnessed_by')} required>
            <Select value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
              <option value="">{tr('ipd', 'select_the_second_nurse')}</option>
              {witnesses.map((nurse) => (
                <option key={nurse.id} value={nurse.id}>
                  {nurse.designation ? `${nurse.name} · ${nurse.designation}` : nurse.name}
                </option>
              ))}
            </Select>
            {nurses.isSuccess && witnesses.length === 0 && <p className="mt-1 text-xs text-destructive">{tr('ipd', 'nobody_else_can_witness_this_dose')}</p>}
          </Field>
        )}

        {status === 'given' && (
          <Field label={tr('ipd', 'given_at')} required>
            <DateTimePicker className="w-full" value={givenAtValue} onChange={setGivenAtValue} max={localDateTimeValue(new Date())} />
          </Field>
        )}

        {status === 'given' && (
          <Field label={tr('ipd', 'site')}>
            <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder={tr('ipd', 'left_forearm_right_deltoid')} />
          </Field>
        )}

        {reasonRequired && (
          <Field label={tr('ipd', 'reason')} required hint={tr('ipd', early ? 'this_dose_is_more_than_an_hour_early_say_why' : 'a_dose_that_was_not_given_needs_an_explanation')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
